import sortBy from "lodash/sortBy";
import { useEffect, useMemo, useState } from "react";

import { useCustomSurfaceTreatments } from "#modules/companySettings/view/useCustomSurfaceTreatments";
import { useBulkDeleteQuoteRequestLineSurfaceTreatments } from "#modules/quoteRequestLines/view/useBulkDeleteQuoteRequestLineSurfaceTreatments";
import { useBulkPredictSurfaceTreatment } from "#modules/quoteRequestLines/view/useBulkPredictSurfaceTreatment";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { useSurfaceTreatments } from "#modules/quoteRequestLines/view/useSurfaceTreatments";
import {
  areDescriptionsEqual,
  createKeyFromDescription,
  IndustrialProcessSchema,
  isSurfaceTreatmentProcessStep,
} from "shared";

export const useSurfaceTreatmentOptions = () => {
  const { customSurfaceTreatments } = useCustomSurfaceTreatments();
  const { surfaceTreatments: modelSurfaceTreatments } = useSurfaceTreatments();

  const allSurfaceTreatments = useMemo(
    () =>
      modelSurfaceTreatments.concat(
        customSurfaceTreatments.map((treatment, index) => ({
          industrialProcess: IndustrialProcessSchema.enum.SURFACE_TREATMENT,
          description: treatment.description,
          processKey: null,
          mode: "TIME" as const,
          index,
        })),
      ),
    [modelSurfaceTreatments, customSurfaceTreatments],
  );

  const data = useMemo(() => {
    const deduplicated = allSurfaceTreatments.filter((treatment, index, self) => {
      return (
        self.findIndex((t) => areDescriptionsEqual(t.description, treatment.description)) === index
      );
    });

    return sortBy(
      deduplicated.map((surfaceTreatment) => ({
        label: surfaceTreatment.description ?? "",
        value: createKeyFromDescription(surfaceTreatment.description),
      })),
      (item) => item.label,
    );
  }, [allSurfaceTreatments]);

  return { data, allSurfaceTreatments };
};

export const useSurfaceTreatmentSelect = (quoteRequestLineIds: string[]) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const { bulkPredictSurfaceTreatment } = useBulkPredictSurfaceTreatment();
  const { bulkDeleteQuoteRequestLineSurfaceTreatments } =
    useBulkDeleteQuoteRequestLineSurfaceTreatments();
  const { quoteRequestLine } = useQuoteRequestLine({ id: quoteRequestLineIds[0] ?? "" });
  const { data, allSurfaceTreatments } = useSurfaceTreatmentOptions();

  const defaultValues = useMemo(() => {
    const steps = quoteRequestLine?.processSteps.filter(isSurfaceTreatmentProcessStep);
    return steps
      ? steps.map((step) => createKeyFromDescription(step.description)).filter(Boolean)
      : [];
  }, [quoteRequestLine?.processSteps]);

  useEffect(() => {
    setSelectedValues(defaultValues);
  }, [defaultValues]);

  const onChange = (updatedValues: string[]) => {
    setSelectedValues(updatedValues);

    if (updatedValues.length > 0) {
      const descriptions = updatedValues
        .map((key) => {
          const treatment = allSurfaceTreatments.find(
            (st) => createKeyFromDescription(st.description) === key,
          );
          return treatment?.description ?? "";
        })
        .filter(Boolean);

      bulkPredictSurfaceTreatment({
        surfaceTreatmentDescriptions: descriptions,
        quoteRequestLineIds,
      });
    } else {
      bulkDeleteQuoteRequestLineSurfaceTreatments(quoteRequestLineIds);
    }
  };

  return {
    data,
    value: selectedValues,
    onChange,
    hasOptions: data.length > 0,
  };
};
